import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";
import { APP_VERSION } from "../version";

import { crashlytics } from "../services/firebase";
export interface VersionConfig {
  isMaintenanceMode: boolean; // When true, blocks all users on this version temporarily
  isVersionDisabled: boolean; // When true, permanently disables this version requiring app update
  message: string; // Message to show when version is blocked or in maintenance
  allowedUsers?: string[]; // User IDs that can bypass maintenance mode
  blockedUsers?: string[]; // User IDs that are blocked from accessing this version
  blockedMessage?: string; // Custom message for blocked users
}

interface UseVersionCheckResult {
  isAllowed: boolean;
  message: string;
  isChecking: boolean;
  config: VersionConfig | null;
  recheckVersion: () => Promise<void>;
}

/**
 * Hook to check version compatibility with Firebase configuration
 * Uses real-time listener to immediately respond to maintenance mode changes
 */
export const useVersionCheck = (userId?: string): UseVersionCheckResult => {
  const [isAllowed, setIsAllowed] = useState(true);
  const [message, setMessage] = useState("");
  const [isChecking, setIsChecking] = useState(true);
  const [config, setConfig] = useState<VersionConfig | null>(null);

  useEffect(() => {
    setIsChecking(true);

    // Real-time listener for version configuration
    const configRef = doc(db, "versions", APP_VERSION);
    const unsubscribe = onSnapshot(
      configRef,
      (snapshot) => {
        console.log(`Version config snapshot received for version ${APP_VERSION}:`, snapshot.data());
        if (!snapshot.exists()) {
          // Version document doesn't exist - block access
          setIsAllowed(false);
          setMessage(
            "This version of the app is no longer supported. Please update to continue using the app.",
          );
          setConfig(null);
          setIsChecking(false);
          return;
        }

        const versionConfig = snapshot.data() as VersionConfig;
        setConfig(versionConfig);

        // Check if user is blocked (takes precedence)
        if (userId && versionConfig.blockedUsers?.includes(userId)) {
          setIsAllowed(false);
          setMessage(
            versionConfig.blockedMessage ||
              "Your access to this app has been restricted. Please contact support for assistance.",
          );
          setIsChecking(false);
          return;
        }

        // Check if user is in allowed list (bypass maintenance mode and version disabled)
        if (userId && versionConfig.allowedUsers?.includes(userId)) {
          setIsAllowed(true);
          setMessage("");
          setIsChecking(false);
          return;
        }

        // Check if version is disabled (permanent block - requires app update)
        if (versionConfig.isVersionDisabled) {
          setIsAllowed(false);
          setMessage(
            "We have introduced new features and improvements. This version is no longer available. Please update to the latest version to continue using the app. You might need to uninstall old version before installing new version.",
          );
          setIsChecking(false);
          return;
        }

        // Check maintenance mode (temporary block)
        if (versionConfig.isMaintenanceMode) {
          setIsAllowed(false);
          setMessage(
            versionConfig.message ||
              "This version is currently undergoing maintenance. Please check back soon.",
          );
          setIsChecking(false);
          return;
        }

        // All checks passed - allow access
        setIsAllowed(true);
        setMessage("");
        setIsChecking(false);
      },
      (error) => {
        crashlytics.logError(
          `Error fetching version config: ${JSON.stringify(error?.message)}`,
          "useVersionCheck.ts",
          error,
        );
        // On error, allow access to avoid blocking users
        setIsAllowed(true);
        setMessage("");
        setConfig(null);
        setIsChecking(false);
      },
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [userId]);

  // Manual recheck function (less important now with real-time listener)
  const recheckVersion = async () => {
    // The real-time listener handles updates automatically
    // This function is kept for backward compatibility but does nothing
  };

  return {
    isAllowed,
    message,
    isChecking,
    config,
    recheckVersion,
  };
};
